import React, { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { CorpHometownTaxOrderFormData, Discount, OrderCreateFormCorpHometownTax } from "interfaces/front/page/orderForm";
import { Field, Formik, FormikProps } from "formik";
import useOrderItems from "lib/hooks/useOrderItems";
import { useFrontAuthContext } from "lib/hoc/withFrontAuth";
import useInitialPayment from "lib/hooks/useInitialPayment";
import * as Yup from "yup";
import { array, object } from "yup";
import { orderCourseOptionSchema } from "lib/validation/orderCourseOptionSchema";
import useOrderFormOnSubmit from "lib/hooks/useOrderFormOnSubmit";
import useCartAmountInfo, { CartAmountInfo } from "lib/hooks/useCartAmountInfo";
import EnhancedEcommerceContext from "lib/contexts/EnhancedEcommerceContext";
import { Confirm, Content } from "components/front/order/form/OrderForm";
import OrderFormSection from "components/front/order/form/OrderFormSection";
import OrderItemField from "components/front/order/form/fields/OrderItemField";
import DiscountsField from "components/front/order/form/DiscountsField";
import CartAmountArea from "components/front/order/form/CartAmountArea";
import DeliveryOptionField from "components/front/order/form/DeliveryOptionField";
import PaymentField from "components/front/order/payment/PaymentField";
import { CorpHometownTaxNote } from "components/front/order/literal/CrowdFundingNote";
import { RuleLiteral } from "components/front/order/literal/RuleLiteral";
import WritesCommentField from "components/front/order/form/fields/WritesCommentField";
import OrderRealizeButtonArea from "components/front/order/form/OrderRealizeButtonArea";
import CorporateAddressField from "components/front/order/recipient/CorporateAddressField";
import { CorporateAddress } from "interfaces/front/business";
import { corporateAddressSchema } from "lib/validation/recipientSchemas";
import useServiceType from "lib/hooks/front/useServiceType";

export type Props = { orderForm: CorpHometownTaxOrderFormData };
const OrderFormCorpHometownTax: FC<Props> = ({ orderForm }) => {
  const { initialValues, validationSchema, onSubmit, isNotCorpHometownTax, isDeliveryOption } = useOrderFormCorpHometownTaxStates(orderForm);
  return (
    <Formik<OrderCreateFormCorpHometownTax>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {(formik) => (
        <OrderFormCorpHometownTaxFieldset orderForm={orderForm} formik={formik}>
          {({ courseIds, cartAmountInfo, handleAddDiscount }) => (
            <Content>
              <OrderFormSection title="応援する商品を確認">
                {formik.values.order_items.map((_orderItem, index) => (
                  <OrderItemField key={index} name={`order_items[${index}]`} cartItems={orderForm.cart_items} project={orderForm.project} />
                ))}
                {formik.values.discounts.length === 0 && isNotCorpHometownTax && (
                  <DiscountsField name="discounts" projectId={orderForm.project.id} onAdd={handleAddDiscount} />
                )}
                <CartAmountArea info={cartAmountInfo} shippingFee={formik.values.delivery_method?.shipping_fee}/>
              </OrderFormSection>
              {cartAmountInfo.isNeededPayment && (
                <OrderFormSection title="お支払い情報の入力">
                  <Field
                    component={PaymentField}
                    name="payment"
                    totalAmount={cartAmountInfo.totalAmount}
                    supportEndAt={orderForm.project.support_end_at}
                    payment={orderForm.payment}
                  />
                </OrderFormSection>
              )}
              {isDeliveryOption && (
                orderForm.project.delivery_options.length !== 1 ? (
                  <OrderFormSection title="商品の受け取り方法を選択">
                    <Field component={DeliveryOptionField} name="delivery_method" delivery_options={orderForm.project} totalAmount={cartAmountInfo.totalAmount} />
                  </OrderFormSection>
                ) : (
                  // 受け取り方法が一つの場合、このエリアは非表示だがロジックは実行する
                  <Field component={DeliveryOptionField} name="delivery_method" delivery_options={orderForm.project} totalAmount={cartAmountInfo.totalAmount} />
                )
              )}
              <OrderFormSection title="お届け先を入力">
                <Field component={CorporateAddressField} name="recipient" courseIds={courseIds} />
              </OrderFormSection>
              <OrderFormSection title="応援前にご確認下さい">
                <Confirm>
                  <CorpHometownTaxNote />
                  <RuleLiteral {...orderForm.project} />
                  <WritesCommentField name="writes_comment" label="応援コメントを投稿" />
                  <OrderRealizeButtonArea />
                </Confirm>
              </OrderFormSection>
            </Content>
          )}
        </OrderFormCorpHometownTaxFieldset>
      )}
    </Formik>
  );
};
export default OrderFormCorpHometownTax;

const OrderFormCorpHometownTaxFieldset: FC<{
  orderForm: CorpHometownTaxOrderFormData;
  formik: FormikProps<OrderCreateFormCorpHometownTax>;
  children: FC<{ courseIds: number[]; cartAmountInfo: CartAmountInfo; handleAddDiscount: (discount: Discount) => void }>;
}> = ({ orderForm, formik, children }) => {
  const courseIds = useMemo(() => formik.values.order_items.map((orderItem) => orderItem.course_id), [formik.values.order_items]);
  const cartAmountInfo = useCartAmountInfo(orderForm.cart_items, orderForm.system_commission.amount, formik.values.discounts);
  const handleAddDiscount = useCallback(
    (discount: Discount) => {
      const willNotNeededPayment = cartAmountInfo.totalAmount <= discount.amount;
      if (willNotNeededPayment) formik.setFieldValue("payment", { method: "no_method" });
    },
    [cartAmountInfo.totalAmount, formik.setFieldValue]
  );
  const { triggerOrderFormEvent, triggerPaymentEvent } = useContext(EnhancedEcommerceContext.OrderForm);
  useEffect(() => {
    triggerOrderFormEvent().catch(console.error);
  }, [triggerOrderFormEvent]);
  useEffect(() => {
    if (!cartAmountInfo.isNeededPayment) triggerPaymentEvent("no_method").catch(console.error);
  }, [cartAmountInfo.isNeededPayment, triggerPaymentEvent]);
  return <fieldset>{children({ courseIds, cartAmountInfo, handleAddDiscount })}</fieldset>;
};

const useOrderFormCorpHometownTaxStates = (orderForm: CorpHometownTaxOrderFormData) => {
  const member = useFrontAuthContext();
  const initialPayment = useInitialPayment(orderForm.payment);
  const orderItems = useOrderItems(orderForm.cart_items, member);
  const { isCorpHometownTax } = useServiceType(orderForm.project.service_type);
  const isNotCorpHometownTax = !isCorpHometownTax;
  const isDeliveryOption = orderForm.project.delivery_options_flag;
  const initialDeliveryMethod = useMemo(() => {
    const deliveryOptions = orderForm.project.delivery_options;
    if (deliveryOptions.length === 1) {
      const selectedOption = deliveryOptions[0];
      return {
        method: selectedOption.method,
        shipping_fee: undefined,
      };
    }
    return undefined;
  }, [orderForm.project.delivery_options]);
  const initialValues: OrderCreateFormCorpHometownTax = {
    service_type: "corp_hometown_tax",
    order_items: orderItems,
    discounts: [],
    onestop_flag: null,
    recipient: orderForm.order_recipient as CorporateAddress,
    delivery_method: initialDeliveryMethod,
    payment: initialPayment,
    writes_comment: true,
  };
  const validationSchema = useMemo(
    () =>
      Yup.object({
        order_items: array().of(
          object().shape({
            details: array().of(
              object().shape({
                course_options: array().defined().of(orderCourseOptionSchema()),
              })
            ),
          })
        ),
        onestop_flag: Yup.boolean().nullable(),
        recipient: corporateAddressSchema,
        delivery_method: isDeliveryOption ? Yup.object().shape({
          method: Yup.string().required("配送方法を選択してください。"),
          shipping_fee: Yup.number().notRequired()
        }).required("配送オプションは必須です。") : Yup.mixed().notRequired(),
        payment: Yup.object().required("必須項目です。"),
        writes_comment: Yup.boolean().defined(),
      }).defined(),
    [isDeliveryOption]
  );
  const onSubmit = useOrderFormOnSubmit(orderForm.project.code);
  return { initialValues, validationSchema, onSubmit, isNotCorpHometownTax, isDeliveryOption };
};
